import {
  createContext,
  useContext,
  useEffect,
  useState,
} from "react";
import type { ReactNode } from "react";
import { UseLivlyLeaseUser } from "shared/api/LivlyUserProvider";
import useChangeTenant from "shared/hooks/useChangeTenant";
import { getCurrentTenantName } from "shared/hooks/TenentProvider";
import { UserTenants } from "shared/types/User";

interface TenantSwitcherContextValue {
  hasAccess: boolean;
  open: boolean;
  openPalette: () => void;
  closePalette: () => void;
  search: string;
  setSearch: (value: string) => void;
  currentTenantName: string;
  isLoading: boolean;
  filteredTenants: UserTenants[];
  selectedTenant: UserTenants | null;
  onSelectTenant: (tenant: UserTenants) => void;
}

// Undefined default forces consumers through the provider so state (and the
// single Ctrl/Cmd+K listener below) is never accidentally duplicated.
const TenantSwitcherContext = createContext<
  TenantSwitcherContextValue | undefined
>(undefined);

// Mount once per authenticated layout (see Layout.tsx) alongside a single
// <TenantSwitcherDialog />. Multiple <TenantSwitcher> trigger buttons can then
// share one open/search state instead of each owning its own dialog + global
// keydown listener.
export function TenantSwitcherProvider({ children }: { children: ReactNode }) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const { mutate, isPending: isLoading } = useChangeTenant();
  const user = UseLivlyLeaseUser();
  const accessToken = localStorage.getItem("access_token");
  const hasAccess = !!(accessToken && user);

  const openPalette = () => setOpen(true);
  const closePalette = () => {
    setOpen(false);
    setSearch("");
  };

  const currentTenantName = getCurrentTenantName(user);
  const tenants = user?.tenants || [];
  const normalizedQuery = search.trim().toLowerCase();
  const filteredTenants = tenants.filter((tenant) =>
    normalizedQuery
      ? tenant.tenant.name.toLowerCase().includes(normalizedQuery) ||
        tenant.tenant.id.toLowerCase().includes(normalizedQuery) ||
        tenant.tenant.externalId?.toLowerCase().includes(normalizedQuery)
      : true,
  );
  const selectedTenant =
    tenants.find((tenant) => tenant.tenant.name === currentTenantName) || null;

  const onSelectTenant = (tenant: UserTenants) => {
    if (!user) return;
    mutate({ id: tenant.tenant.id, currentId: user.id });
    closePalette();
  };

  useEffect(() => {
    if (!hasAccess) {
      return;
    }

    const onKeyDown = (event: KeyboardEvent) => {
      if (
        !(event.metaKey || event.ctrlKey) ||
        event.key.toLowerCase() !== "k"
      ) {
        return;
      }

      const target = event.target as HTMLElement | null;
      const isTypingTarget =
        target?.tagName === "INPUT" ||
        target?.tagName === "TEXTAREA" ||
        target?.tagName === "SELECT" ||
        target?.isContentEditable;
      if (isTypingTarget && !open) {
        return;
      }

      event.preventDefault();
      setOpen(true);
    };

    window.addEventListener("keydown", onKeyDown);
    return () => {
      window.removeEventListener("keydown", onKeyDown);
    };
  }, [hasAccess, open]);

  return (
    <TenantSwitcherContext.Provider
      value={{
        hasAccess,
        open,
        openPalette,
        closePalette,
        search,
        setSearch,
        currentTenantName,
        isLoading,
        filteredTenants,
        selectedTenant,
        onSelectTenant,
      }}
    >
      {children}
    </TenantSwitcherContext.Provider>
  );
}

export function useTenantSwitcher() {
  const ctx = useContext(TenantSwitcherContext);
  if (!ctx) {
    throw new Error(
      "useTenantSwitcher must be used within a TenantSwitcherProvider",
    );
  }
  return ctx;
}
